import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {offerAccessMessages} from 'components/Offer/Shared/messages'
import {RoomLabels, RoomLabelsText} from 'components/Offer/Shared/RoomLabels'
import {YourChoiceLabel} from 'components/Offer/Shared/YourChoiceLabel'
import {getOfferAccessFlowSettings} from 'modules/sapiSearch/selectors'
import {Hotel} from 'modules/sapiSearch/slice'

import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {OfferSecondaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'

interface MainProps {
  hasRoomLabels?: boolean
  variant: OfferSecondaryLayoutVariant
  offer: SearchOffer
  isSplitBooking?: false
  isYourChoice?: never
  hotelId: Hotel['objectID']
  isAppLockedDeal?: boolean
}

interface SplitBookingProps {
  isSplitBooking: true
  variant: OfferSecondaryLayoutVariant
  isYourChoice: boolean
  hasRoomLabels?: never
  offer?: never
  hotelId?: never
  isAppLockedDeal?: never
}

const getOfferAccessFlowLabel = ({
  isAppLocked,
  isAuthLocked
}: {
  isAppLocked: boolean
  isAuthLocked: boolean
}) => {
  if (isAppLocked) return offerAccessMessages.appLocked
  if (isAuthLocked) return offerAccessMessages.signInToBookLabel
  return null
}

export const RoomLabelsComponent: React.FC<MainProps | SplitBookingProps> = ({
  hasRoomLabels = true,
  variant = 'SecondaryDefault',
  offer,
  isSplitBooking,
  isYourChoice = false,
  hotelId,
  isAppLockedDeal
}) => {
  const {formatMessage} = useIntl()
  const {isAppRequiredUnlocked, isAuthRequiredUnlocked} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, offer?.id)
  )

  const offerAccessFlowLabel = getOfferAccessFlowLabel({
    isAppLocked: isAppRequiredUnlocked,
    isAuthLocked: isAuthRequiredUnlocked
  })

  const translatedOfferAccessFlowLabel = offerAccessFlowLabel
    ? formatMessage(offerAccessFlowLabel)
    : null

  if (variant === 'SecondaryInline' && hasRoomLabels && !isSplitBooking)
    return (
      <RoomLabelsText
        offer={offer}
        translatedOfferAccessFlowLabel={translatedOfferAccessFlowLabel}
      />
    )

  if (isSplitBooking) {
    return (
      <Box display="flex" gap="s200" flexWrap="wrap">
        {isYourChoice && (
          <Box display="flex" whiteSpace="nowrap" gap="s200">
            <YourChoiceLabel />
            {variant === 'SecondaryDefault' && <span>·</span>}
          </Box>
        )}
        {variant === 'SecondaryDefault' && (
          <Box whiteSpace="nowrap">
            <Text variant="bodyXS" colorPath="content.neutral.secondary">
              <FormattedMessageWrapper
                id="roomSwitchRequired"
                defaultMessage="Room switch required"
              />
            </Text>
          </Box>
        )}
      </Box>
    )
  }

  if (isAppLockedDeal)
    return (
      <Text variant="bodyXS" colorPath="content.neutral.secondary">
        <FormattedMessageWrapper
          id="downloadAppAccessPrice"
          defaultMessage="Download our app - access this price"
        />
      </Text>
    )

  return (
    <>
      {hasRoomLabels && <RoomLabels isAlternativeColor offer={offer} />}
      {translatedOfferAccessFlowLabel && (
        <Text variant="bodyXS" colorPath="content.neutral.secondary">
          {translatedOfferAccessFlowLabel}
        </Text>
      )}
    </>
  )
}
